/**
 * Untamed Theme - Offline License Validation
 * Supports:
 * - Legacy lifetime keys (UNTMD)
 * - Domain-locked keys with RSA signature (UNTMD2)
 */
(function() {
  'use strict';

  var CONFIG = {
    DEBUG: false
  };

  // Hardcoded values to keep theme settings minimal.
  var ACTIVATION_BASE_URL = 'https://console.buyuntamed.com/activate';

  var FALLBACK_PUBLIC_KEY_PEM = [
    '-----BEGIN PUBLIC KEY-----',
    'MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAuXd64B5suHrSljlWuw+I',
    '6mql7HQddJ6uhNVoYwdwy0he0nsMcj66g80hj7QX61zF20GgOlrmYwKBOdCoaKse',
    'yn+ppDzDU0zA5z+4Gfx5s2XhX+vBcjj+R7nEFa81ZXg9y9XSz1BPObJdUxhpN1DS',
    'dzv6EIrnlmnfoi7tgciPqPJbbXNe++qsbextgQY16cKxwB33MsbEnG2wsXtUo021',
    'OOwFcr8Qxf2cEolNQPtb9dQ/s4rchS95BCRlq1UrApyX80N5LiudhoXCSEaPY2ZB',
    'l5twvVXZIePpN1FAy0i5ioicfCHUskNVuvTaEBrJTaDs4tNVSMxel306Zngva0ez',
    'ywIDAQAB',
    '-----END PUBLIC KEY-----'
  ].join('\n');

  function log() {
    if (CONFIG.DEBUG && typeof console !== 'undefined') {
      try {
        console.log.apply(console, ['[Untamed License]'].concat([].slice.call(arguments)));
      } catch (_) {}
    }
  }

  function normalizeDomain(input) {
    return String(input || '')
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/+$/, '');
  }

  function toBase64(str) {
    var base64 = str.replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4) base64 += '=';
    return base64;
  }

  function base64UrlDecodeJson(str) {
    try {
      return JSON.parse(atob(toBase64(str)));
    } catch (_) {
      return null;
    }
  }

  function base64UrlToUint8Array(str) {
    var bin = atob(toBase64(str));
    var bytes = new Uint8Array(bin.length);
    var i;
    for (i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    return bytes;
  }

  function parsePemToArrayBuffer(pem) {
    var body = String(pem || '')
      .replace(/-----BEGIN PUBLIC KEY-----/g, '')
      .replace(/-----END PUBLIC KEY-----/g, '')
      .replace(/\s+/g, '');

    var raw = atob(body);
    var bytes = new Uint8Array(raw.length);
    var i;
    for (i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
    return bytes.buffer;
  }

  function getActivationUrl() {
    var settings = window.UntamedThemeSettings || {};
    var baseUrl = String(ACTIVATION_BASE_URL).trim();
    var shop = normalizeDomain(settings.shopPermanentDomain || '');
    if (!shop) return baseUrl;
    var separator = baseUrl.indexOf('?') === -1 ? '?' : '&';
    return baseUrl + separator + 'shop=' + encodeURIComponent(shop);
  }

  function getPublicKeyPem() {
    return FALLBACK_PUBLIC_KEY_PEM;
  }

  function detectLicenseType(licenseKey) {
    var parts = String(licenseKey || '').split('.');
    if (parts.length !== 3) return 'unknown';
    var header = base64UrlDecodeJson(parts[0]);
    if (!header || !header.typ) return 'unknown';
    return header.typ;
  }

  function verifyLegacyLifetimeLicense(licenseKey) {
    try {
      if (!licenseKey || typeof licenseKey !== 'string') {
        return { valid: false, reason: 'missing' };
      }

      var parts = licenseKey.split('.');
      if (parts.length !== 3) {
        return { valid: false, reason: 'format' };
      }

      var header = base64UrlDecodeJson(parts[0]);
      var payload = base64UrlDecodeJson(parts[1]);

      if (!header || !payload) {
        return { valid: false, reason: 'decode' };
      }

      if (header.typ !== 'UNTMD') {
        return { valid: false, reason: 'type_header' };
      }

      if (payload.type !== 'lifetime') {
        return { valid: false, reason: 'not_lifetime' };
      }

      if (payload.product !== 'untamed-theme') {
        return { valid: false, reason: 'wrong_product' };
      }

      if (typeof payload.exp === 'number' && Math.floor(Date.now() / 1000) > payload.exp) {
        return { valid: false, reason: 'expired' };
      }

      return { valid: true, payload: payload };
    } catch (_) {
      return { valid: false, reason: 'error' };
    }
  }

  async function verifyDomainLockedLicense(licenseKey, expectedShopDomain) {
    try {
      var parts = String(licenseKey || '').split('.');
      if (parts.length !== 3) return { valid: false, reason: 'format' };

      var encodedHeader = parts[0];
      var encodedPayload = parts[1];
      var encodedSignature = parts[2];

      var header = base64UrlDecodeJson(encodedHeader);
      var payload = base64UrlDecodeJson(encodedPayload);

      if (!header || !payload) return { valid: false, reason: 'decode' };
      if (header.typ !== 'UNTMD2') return { valid: false, reason: 'type_header' };
      if (header.alg !== 'RS256') return { valid: false, reason: 'alg' };
      if (payload.product !== 'untamed-theme') return { valid: false, reason: 'wrong_product' };
      if (payload.type !== 'lifetime' && payload.type !== 'subscription') {
        return { valid: false, reason: 'unsupported_type' };
      }

      // For non-lifetime (1-Month / time-limited) keys, exp is required and enforced.
      if (payload.type === 'subscription') {
        if (typeof payload.exp !== 'number') return { valid: false, reason: 'missing_exp' };
        if (Math.floor(Date.now() / 1000) > payload.exp) {
          return { valid: false, reason: 'expired' };
        }
      } else if (typeof payload.exp === 'number' && Math.floor(Date.now() / 1000) > payload.exp) {
        // Lifetime keys: exp is optional, but if set we still honor it.
        return { valid: false, reason: 'expired' };
      }

      if (normalizeDomain(payload.shop) !== normalizeDomain(expectedShopDomain)) {
        return { valid: false, reason: 'domain_mismatch' };
      }

      if (!window.crypto || !window.crypto.subtle || !window.TextEncoder) {
        return { valid: false, reason: 'crypto_unavailable' };
      }

      var key = await window.crypto.subtle.importKey(
        'spki',
        parsePemToArrayBuffer(getPublicKeyPem()),
        { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
        false,
        ['verify']
      );

      var data = new TextEncoder().encode(encodedHeader + '.' + encodedPayload);
      var signature = base64UrlToUint8Array(encodedSignature);

      var isValidSignature = await window.crypto.subtle.verify(
        { name: 'RSASSA-PKCS1-v1_5' },
        key,
        signature,
        data
      );

      if (!isValidSignature) return { valid: false, reason: 'signature' };
      return { valid: true, payload: payload };
    } catch (_) {
      return { valid: false, reason: 'error' };
    }
  }

  async function verifyDomainLockedSubscriptionLicense(licenseKey, expectedShopDomain) {
    try {
      var parts = String(licenseKey || '').split('.');
      if (parts.length !== 3) return { valid: false, reason: 'format' };

      var encodedHeader = parts[0];
      var encodedPayload = parts[1];
      var encodedSignature = parts[2];

      var header = base64UrlDecodeJson(encodedHeader);
      var payload = base64UrlDecodeJson(encodedPayload);

      if (!header || !payload) return { valid: false, reason: 'decode' };
      if (header.typ !== 'UNTMD3') return { valid: false, reason: 'type_header' };
      if (header.alg !== 'RS256') return { valid: false, reason: 'alg' };
      if (payload.product !== 'untamed-plus') return { valid: false, reason: 'wrong_product' };
      if (payload.type !== 'subscription') return { valid: false, reason: 'not_subscription' };
      if (payload.tier && payload.tier !== 'ai') return { valid: false, reason: 'wrong_tier' };

      if (typeof payload.exp !== 'number') return { valid: false, reason: 'missing_exp' };
      if (Math.floor(Date.now() / 1000) > payload.exp) {
        return { valid: false, reason: 'expired' };
      }

      if (normalizeDomain(payload.shop) !== normalizeDomain(expectedShopDomain)) {
        return { valid: false, reason: 'domain_mismatch' };
      }

      if (!window.crypto || !window.crypto.subtle || !window.TextEncoder) {
        return { valid: false, reason: 'crypto_unavailable' };
      }

      var key = await window.crypto.subtle.importKey(
        'spki',
        parsePemToArrayBuffer(getPublicKeyPem()),
        { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
        false,
        ['verify']
      );

      var data = new TextEncoder().encode(encodedHeader + '.' + encodedPayload);
      var signature = base64UrlToUint8Array(encodedSignature);

      var isValidSignature = await window.crypto.subtle.verify(
        { name: 'RSASSA-PKCS1-v1_5' },
        key,
        signature,
        data
      );

      if (!isValidSignature) return { valid: false, reason: 'signature' };
      return { valid: true, payload: payload };
    } catch (_) {
      return { valid: false, reason: 'error' };
    }
  }

  function showOverlay(title, message) {
    try {
      var existing = document.getElementById('untamed-license-overlay');
      if (existing) existing.remove();

      var overlay = document.createElement('div');
      overlay.id = 'untamed-license-overlay';
      overlay.setAttribute('style', [
        'position: fixed',
        'inset: 0',
        'background: rgba(255, 255, 255, 0.98)',
        'z-index: 2147483647',
        'display: flex',
        'align-items: center',
        'justify-content: center',
        'font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
      ].join(';'));

      overlay.innerHTML = [
        '<div style="text-align:center;padding:40px;max-width:620px">',
        '<h2 style="margin:0 0 12px 0;color:#333;font-size:24px;font-weight:600">' + (title || 'License Required') + '</h2>',
        '<p style="margin:0 0 20px 0;color:#666;font-size:16px;line-height:1.5">' + (message || 'Please configure your theme license key in theme settings.') + '</p>',
        '<p style="margin:0 0 20px 0;color:#999;font-size:14px">',
        'Go to <strong>Online Store -> Themes -> Customize -> Theme Settings -> License Configuration</strong>',
        '</p>',
        '<a href="' + getActivationUrl() + '" target="_blank" rel="noopener noreferrer" style="display:inline-block;background:#111827;color:#fff;text-decoration:none;padding:12px 22px;border-radius:999px;font-size:14px;font-weight:600;margin-bottom:12px;">Open License Activation</a>',
        '<p style="margin:8px 0 0 0;color:#999;font-size:13px">',
        'Need help? Contact <a href="mailto:support@buyuntamed.com" style="color:#165DFF">support@buyuntamed.com</a>',
        '</p>',
        '</div>'
      ].join('');

      document.body.appendChild(overlay);
      document.body.style.overflow = 'hidden';
    } catch (_) {}
  }

  function showOverlayWhenReady(title, message) {
    if (document.body) {
      showOverlay(title, message);
    } else {
      document.addEventListener('DOMContentLoaded', function() {
        showOverlay(title, message);
      });
    }
  }

  function enableTheme() {
    log('Theme enabled - valid license');
  }

  async function init() {
    if (!window.UntamedThemeSettings) {
      setTimeout(init, 100);
      return;
    }

    var settings = window.UntamedThemeSettings;
    var key = String(settings.licenseKey || '').trim();
    var shopDomain = normalizeDomain(settings.shopPermanentDomain || '');

    if (!key) {
      showOverlayWhenReady(
        'Theme License Required',
        'Please generate and enter your license key to activate this theme.'
      );
      return;
    }

    var type = detectLicenseType(key);
    var result;

    if (type === 'UNTMD3') {
      result = await verifyDomainLockedSubscriptionLicense(key, shopDomain);
    } else if (type === 'UNTMD2') {
      result = await verifyDomainLockedLicense(key, shopDomain);
    } else {
      // Grandfathered behavior for old buyers
      result = verifyLegacyLifetimeLicense(key);
    }

    if (!result.valid) {
      var errorMessages = {
        missing: 'License key is missing.',
        format: 'Invalid license key format.',
        decode: 'Unable to decode license key.',
        type_header: 'Invalid license type.',
        alg: 'Unsupported license signing algorithm.',
        signature: 'License signature verification failed.',
        not_lifetime: 'This theme requires a lifetime license.',
        not_subscription: 'This theme requires an active subscription license.',
        unsupported_type: 'License type is not supported by this theme.',
        missing_exp: 'Subscription license is missing expiry information.',
        wrong_tier: 'License tier is not valid for this theme.',
        wrong_product: 'License is not valid for this product.',
        expired: 'License has expired.',
        domain_mismatch: 'License key is not valid for this Shopify store domain.',
        crypto_unavailable: 'This browser cannot verify the license key offline.',
        error: 'Error validating license.'
      };

      var message = errorMessages[result.reason] || 'Invalid license key.';
      showOverlayWhenReady('Invalid License', message + ' Use License Activation to retrieve or generate the correct key for this domain.');
      return;
    }

    enableTheme();
  }

  init();
})();

